// Implements: F-IDN-026
/**
 * The invitations just created (v3 `openInvite` success): one read-only link per address, each
 * with "Copiar", shown only now because the server keeps no clear token. An address the server
 * refused is named with the server's reason instead of a link. Owner P2: an invitation the server also
 * e-mailed says so ("Enviamos la invitación por correo a X") and keeps its link; the copy-the-link
 * message stays while any link still has to travel by hand.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { LinkField } from "./LinkField";
import styles from "./Organization.module.css";

export type IssuedEntry =
  | { readonly email: string; readonly link: string; readonly emailed: boolean; readonly problem?: undefined }
  | { readonly email: string; readonly problem: string; readonly link?: undefined };

export interface IssuedLinksProps {
  readonly entries: readonly IssuedEntry[];
  /** The address whose link was just copied, or `null`. */
  readonly copied: string | null;
  readonly onCopy: (entry: { readonly email: string; readonly link: string }) => void;
  readonly onClose: () => void;
}

export function IssuedLinks({ entries, copied, onCopy, onClose }: IssuedLinksProps): ReactElement {
  const { t } = useTranslation();
  const byHand = entries.some((entry) => entry.link !== undefined && !entry.emailed);
  return (
    <Dialog
      open
      title={t("organization.issued.title")}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("organization.issued.done")}
        </Button>
      }
    >
      <div className={styles.form}>
        {byHand ? <p className={styles.status}>{t("organization.issued.body")}</p> : null}
        {entries.map((entry, index) =>
          entry.link === undefined ? (
            <p key={entry.email} className={styles.problem} role="alert">
              {t("organization.invite.partial", { email: entry.email, message: entry.problem })}
            </p>
          ) : (
            <div key={entry.email}>
              <span className={styles.label}>{entry.email}</span>
              {entry.emailed ? (
                <p className={styles.status}>{t("organization.issued.emailed", { email: entry.email })}</p>
              ) : null}
              <LinkField
                id={`issued-link-${String(index)}`}
                label={t("organization.invitations.linkFor", { email: entry.email })}
                value={entry.link}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      onCopy({ email: entry.email, link: entry.link });
                    }}
                  >
                    <Icon name={copied === entry.email ? "check" : "copy"} size="sm" />
                    {t("organization.issued.copy")}
                  </Button>
                }
              />
            </div>
          ),
        )}
      </div>
    </Dialog>
  );
}
